import React, { useState, useEffect, useCallback, createContext, useContext, useMemo, useRef } from 'react';

import './personalisation-typedefs.js';
import { personalisationData } from '../gateway/personalisationData.js';
import { addToCart } from '../gateway/addToCart.js';
import { baseProductConfigurationSlotId, baseProductConfigurationStepId } from '../utilities/strings.js';
import {
  setProductContext,
  trackPersonalisationStepComplete,
  trackVariantSelected,
  trackSlotPopulated,
  trackSlotRemoved,
  trackPersonalisationComplete
} from '../utilities/tracking.js';
import { calculateFinalPriceModifier } from '../utilities/priceModifier';
import {
  getPriceModifiersForVariant,
  resolveSelectionComponentGroupId
} from '../utilities/resolveComponentGroupForVariant.js';
import {
  useCacheFilteredPersonalisationStepsAndSlots,
  useHideSteps,
  hideSlotSelections,
  setAllHiddenToFalse
} from '../utilities/hideSlotsAndSteps.js';
import { wait } from '../utilities/wait.js';
import { hasPersonalisationOptions as checkHasPersonalisationOptions } from '../utilities/hasPersonalisationOptions.js';
import { processBaseAttributes } from '../utilities/processLineItems.js';
import { extractPersonalisationFromUrl, clearPersonalisationFromUrl } from '../utilities/urlSharing.js';
import { useVisualisationUrlManagement } from '../utilities/useVisualisationUrlManagement.js';
import { buildChoicesFromSelections } from '../utilities/buildChoicesFromSelections.js';
import { cropMaskedImageWithPadding } from '../utilities/cropMaskedImageWithPadding.js';
import { logError, logInfo, logWarning } from '../utilities/logging.js';
import { readUvInsertsBootstrapPayload } from '@uv-inserts/read-uv-inserts-bootstrap-payload';
import { resolveUvInserts } from '@shared/view/uv-inserts/resolve-uv-inserts';

/** @typedef {import('./personalisation-typedefs.js').Product} Product */
/** @typedef {import('./personalisation-typedefs.js').Selections} Selections */
/** @typedef {import('./personalisation-typedefs.js').SelectionSlot} SelectionSlot */

// Create the context
export const PersonalisationContext = createContext({
  currentStepId: '',
  setCurrentStepId: /** @type {function(string): void} */ (() => {}),
  currentSlotId: 0,
  setCurrentSlotId: /** @type {function(number): void} */ (() => {}),
  personalisation: null,
  personalisationSteps: new Map(),
  personalisationSlots: new Map(),
  selectedVariantId: null,
  setSelectedVariantId: /** @type {function(any): void} */ (() => {}),
  variants: new Map(),
  addonGroups: new Map(),
  addonItems: new Map(),
  componentGroups: new Map(),
  loading: false,
  isAddingToCart: false,
  isRetrying: false,
  /** @type {Selections} */
  selections: {
    productVariantId: null,
    slots: []
  },
  /** @type {function(Selections): void} */
  setSelections: () => {},
  /** @type {function(Selections | function, Object): void} */
  setSelectionsWithOptions: () => {},
  product: /** @type {Product | null} */ (null),
  currentTotalPrice: 0,
  setCurrentTotalPrice: /** @type {function(number): void} */ (() => {}),
  /** @type {function({ slotId: any, itemSelected: any, groupLineItemPropertyKey?: any, extraImages?: any, supportingImage?: any, componentGroupId?: string | null }):void} */
  updateSelections: () => {},
  /** @type {function({ country: string, onSuccess?: function }):void} */
  updateTheCart: () => {},
  /**
   * Reorders the slot values in selections based on a new order of slot IDs.
   * @param {object} props - The component props.
   * @param {string[]} props.newOrder - Array of slot IDs in the new order.
   */
  reorderSlotValues: () => {},
  setStart: /** @type {function(boolean): void} */ (() => {}),
  personalisationModalOpen: false,
  setPersonalisationModalOpen: /** @type {function(boolean): void} */ (() => {}),
  start: false,
  /** @type {function():void} */
  resetJourney: () => {},
  isResetting: false,
  personalisationId: null,
  /** @type {"FIXED" | "SEQUENTIAL"} */
  visualiserSlotMode: 'FIXED',
  /** @type {{ url: string, visualiserSlotMode: string, groupId: string, file: File | null }[]} */
  visualiserImageUrl: [],
  /** @type {Map<string, { label: string; visualisationType: string, supportingImageLineItemPropertyKey: string }>} */
  visualisationGroups: new Map(),
  lastModifiedGroupId: null,
  hasPersonalisationOptions: false,
  personalisationValidationErrors: [],
  setPersonalisationValidationErrors: /** @type {React.Dispatch<React.SetStateAction<string[]>>} */ (() => {}),
  isSharedPersonalisation: false,
  /** @type {import('@shared/view/uv-inserts/resolve-uv-inserts').ResolvedUvInserts | null} */
  resolvedUvInserts: null,
  activeUvInsertChooser: /** @type {any} */ (null),
  setActiveUvInsertChooser: /** @type {React.Dispatch<React.SetStateAction<any>>} */ (() => {})
});

/**
 * Provides personalisation context to children.
 * @param {{ children: any }} props
 * @returns {JSX.Element}
 */
export const PersonalisationProvider = ({ children }) => {
  const [personalisationModalOpen, setPersonalisationModalOpen] = useState(false);
  // Current state
  const [currentStepId, setCurrentStepId] = useState('');
  const [previousStepId, setPreviousStepId] = useState('');
  const [currentSlotId, setCurrentSlotId] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [currentTotalPrice, setCurrentTotalPrice] = useState(0);
  const [selections, setSelections] = useState(
    /** @type {Selections} */ ({
      productVariantId: null,
      slots: []
    })
  );
  // Variants
  const [variants, setVariants] = useState(new Map());

  // Personalisation ui (populated via Storefront API)
  const [personalisation, setPersonalisation] = useState(null);
  const [personalisationSteps, setPersonalisationSteps] = useState(new Map());
  const [personalisationSlots, setPersonalisationSlots] = useState(new Map());
  const [addonGroups, setAddonGroups] = useState(new Map());
  const [addonItems, setAddonItems] = useState(new Map());
  const [componentGroups, setComponentGroups] = useState(new Map());
  const [loading, setLoading] = useState(/** @type {boolean} */ (false));
  const [dataLoaded, setDataLoaded] = useState(/** @type {boolean} */ (false));
  const [product, setProduct] = useState(/** @type {Product | null} */ (null));
  const [start, setStart] = useState(false);
  const [hideSlotsOnVariantsSettings, setHideSlotsOnVariantsSettings] = useState(new Map());
  const [isResetting, setIsResetting] = useState(false);
  const [visualiserSlotMode, setVisualiserSlotMode] = useState('FIXED');
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasPersonalisationOptions, setHasPersonalisationOptions] = useState(false);
  const [personalisationValidationErrors, setPersonalisationValidationErrors] = useState(/** @type {string[]} */ ([]));
  const [isSharedPersonalisation, setIsSharedPersonalisation] = useState(false);
  /** @type {[import('@shared/view/uv-inserts/resolve-uv-inserts').ResolvedUvInserts | null, Function]} */
  const [resolvedUvInserts, setResolvedUvInserts] = useState(null);
  const [activeUvInsertChooser, setActiveUvInsertChooser] = useState(/** @type {any} */ (null));
  // Ensure URL-driven initialisation (apply selections, jump to last, open modal) only runs once.
  const hasAppliedSharedUrlInitRef = useRef(false);

  /**
   * Validates personalisation selections from URL against current product data.
   * @param {Object} options - Options object.
   * @param {Object} options.urlSelections - Selections from URL.
   * @param {Map} options.personalisationSlots - Current personalisation slots.
   * @param {Map} options.variants - Current variants.
   * @returns {Array} - Array of validation error messages.
   */
  const validatePersonalisationSelections = useCallback(({ urlSelections, personalisationSlots, variants }) => {
    const errors = [];

    if (!urlSelections || !urlSelections.slots) {
      return ['Invalid personalisation data in URL'];
    }

    // Check if product variant is still available
    if (urlSelections.productVariantId) {
      const baseProductVariant = variants.get(urlSelections.productVariantId);
      if (!baseProductVariant || !baseProductVariant.availableForSale) {
        errors.push('The selected product variant is no longer available');
      }
    }

    // Check each slot selection
    urlSelections.slots.forEach((slot) => {
      const currentSlot = personalisationSlots.get(slot.id);

      if (!currentSlot) {
        errors.push(`Personalisation option "${slot.lineItemPropertyKey || 'Unknown'}" is no longer available`);
        return;
      }

      // Validate based on slot type
      if (slot.slotType === 'COMPONENT' && slot.selected) {
        const variant = variants.get(slot.selected);
        if (!variant || !variant.availableForSale) {
          errors.push(`Selected component for "${slot.lineItemPropertyKey || 'Unknown'}" is no longer available`);
        }
      }

      if (slot.slotType === 'ALPHANUMERIC' && slot.selected) {
        // ALPHANUMERIC slots are generally always valid if they have text
        if (typeof slot.selected === 'object' && !slot.selected.text) {
          errors.push(`Text personalisation for "${slot.lineItemPropertyKey || 'Unknown'}" is invalid`);
        }
      }
    });

    return errors;
  }, []);

  /**
   * Enhanced setSelections function with populateDefaultState support.
   * @param {Selections | ((prev: Selections) => Selections)} newSelections - New selections or function to update selections.
   * @param {Object} [options] - Options object.
   * @param {boolean} [options.populateDefaultState] - Whether to populate from URL and validate selections.
   */
  const setSelectionsWithOptions = useCallback(
    (newSelections, options = {}) => {
      const { populateDefaultState = false } = options;

      if (populateDefaultState) {
        // Extract personalisation data from URL
        const urlSelections = extractPersonalisationFromUrl();

        if (urlSelections) {
          // Validate the URL selections against current product data
          const validationErrors = validatePersonalisationSelections({
            urlSelections,
            personalisationSlots,
            variants
          });

          // Merge URL selections with existing selections to preserve all slots.
          // This allows users to select slots that weren't in the shared design.
          setSelections((prev) => {
            // Create a map of URL selections by slot ID for quick lookup.
            const urlSelectionsMap = new Map(urlSelections.slots.map((slot) => [slot.id, slot]));

            // Merge: use URL selection if it exists, otherwise keep the existing slot.
            const mergedSlots = prev.slots.map((existingSlot) => {
              const urlSlot = urlSelectionsMap.get(existingSlot.id);
              if (urlSlot) {
                // Slot exists in URL - use the URL selection.
                return {
                  ...existingSlot,
                  selected: urlSlot.selected,
                  componentGroupId: urlSlot.componentGroupId || existingSlot.componentGroupId,
                  lineItemPropertyKey: urlSlot.lineItemPropertyKey || existingSlot.lineItemPropertyKey,
                  translatedLineItemPropertyKey:
                    urlSlot.translatedLineItemPropertyKey || existingSlot.translatedLineItemPropertyKey
                };
              }
              // Slot not in URL - keep existing (null selection, allowing user to select).
              return existingSlot;
            });

            return {
              ...prev,
              productVariantId: urlSelections.productVariantId || prev.productVariantId,
              slots: mergedSlots
            };
          });

          if (validationErrors.length > 0) {
            // Store validation errors for display in the UI
            setPersonalisationValidationErrors(validationErrors);
          } else {
            // Clear any previous errors
            setPersonalisationValidationErrors([]);
          }
        } else {
          // No URL data, use the provided selections
          setSelections(newSelections);
        }
      } else {
        // Normal setSelections behavior
        setSelections(newSelections);
      }
    },
    [personalisationSlots, variants, validatePersonalisationSelections]
  );

  const [visualisationGroups, setVisualisationGroups] = useState(new Map());
  const [lastModifiedGroupId, setLastModifiedGroupId] = useState(null);

  useEffect(() => {
    if (!window?.personalisation?.product?.id) {
      throw new Error(
        'Personalisation product ID is not set. Please set the VITE_PERSONALISATION_PRODUCT_ID environment variable.'
      );
    }
    // Grab the product from the window object.
    // @ts-expect-error - This is an external object, so we can't type it.
    const productData = window.personalisation.product;
    setProduct(productData);
    // Set the product context for tracking.
    setProductContext(productData);

    // Check for URL parameters that should auto-start personalisation
    const urlParams = new URLSearchParams(window.location.search);
    const hasPersonalisationData = urlParams.has('personalisation');
    const openPersonalisationParam = urlParams.get('openPersonalisation');
    const shouldOpenModal = openPersonalisationParam === 'true';
    const explicitlyDisabled = openPersonalisationParam === 'false';

    // Only auto-start if we have personalisation data OR explicitly told to open,
    if ((hasPersonalisationData || shouldOpenModal) && !explicitlyDisabled) {
      setStart(true);
    }
  }, []);

  useEffect(() => {
    if (product && !loading && currentStepId !== previousStepId) {
      const stepNumber = Array.from(personalisationSteps.keys()).indexOf(currentStepId);
      trackPersonalisationStepComplete({
        stepNumber,
        stepName: currentStepId,
        totalSteps: personalisationSteps.size
      });
      setPreviousStepId(currentStepId);
    }
  }, [currentStepId, loading, product, personalisationSteps, previousStepId]);

  useEffect(() => {
    if (start && !dataLoaded) {
      async function fetchAll() {
        setLoading(true);
        const countryCode = window.Shopify?.country || 'GB';

        if (product === null) {
          // Bail out if we don't have a product yet.
          return;
        }

        if (!product.id) {
          throw new Error('No product ID found in personalisation');
        }

        if (!countryCode) {
          throw new Error('No country code found in personalisation');
        }

        const {
          personalisationDevModel,
          stepsDevModel,
          slotsDevModel,
          addonGroupsDevModel,
          addonItemsDevModel,
          componentGroupsDevModel,
          variantsDevModel,
          hideSlotsOnVariantsSettings
        } = await personalisationData({ productId: product.id, country: countryCode });

        setVariants(variantsDevModel);
        setHideSlotsOnVariantsSettings(hideSlotsOnVariantsSettings);
        const firstStepId = Array.from(stepsDevModel.keys())[0];
        if (variantsDevModel.size === 1) {
          // @TODO: Check why this doesn't have the extra config - what is this?
          setPersonalisationSteps(stepsDevModel);
          // Ensure currentStepId is set to the first step if not already set and steps exist.
          if (!currentStepId && stepsDevModel.size > 0) {
            setCurrentStepId(firstStepId);
          }
          setSelectedVariantId(variantsDevModel.entries().next().value.id);
        } else {
          setPersonalisationSteps(stepsDevModel);

          // set to the first step
          setCurrentStepId(firstStepId);
          setPreviousStepId(firstStepId);
        }

        setHasPersonalisationOptions(
          checkHasPersonalisationOptions({
            stepsDevModel,
            slotsDevModel,
            componentGroupsDevModel,
            variantsDevModel
          })
        );

        setPersonalisation(personalisationDevModel);
        setPersonalisationSlots(slotsDevModel);
        setAddonGroups(addonGroupsDevModel);
        setAddonItems(addonItemsDevModel);
        setComponentGroups(componentGroupsDevModel);

        const hasBaseProductConfiguration = slotsDevModel.get(baseProductConfigurationSlotId);
        const slotValues = Array.from(slotsDevModel.values());

        // Bootstrap UV inserts data if any slot in this personalisation requires it.
        const hasUvInsertSlot = slotValues.some(({ slotType }) => slotType === 'UV_INSERT');
        if (hasUvInsertSlot) {
          const uvPayload = readUvInsertsBootstrapPayload();
          setResolvedUvInserts(resolveUvInserts({ payload: uvPayload }));
        }

        // Legacy visualisation slot mode detection.
        setVisualiserSlotMode(slotValues.some((slot) => slot.slotMode === 'SEQUENTIAL') ? 'SEQUENTIAL' : 'FIXED');

        // Set up the selections object for later use.
        setSelections((prev) => ({
          ...prev,
          // Check if the base product configuration slot exists and if not, set the
          // product variant id to the first variant as there should only be one.
          productVariantId: hasBaseProductConfiguration ? null : product.variants[0].id,

          slots: slotValues.map(({ ...slot }) => ({
            ...slot,
            selected: null,
            componentGroupId: null,
            hidden: false,
            supportingImageLineItemPropertyKey: slot.visualisationGroup?.supportingImageLineItemPropertyKey || null
          }))
        }));
        setLoading(false);
        setDataLoaded(true);

        // If there's only one variant, we don't need to track the variant selected event.
        if (variantsDevModel.size > 1) {
          const {
            id: variantId,
            price: variantPrice,
            title: variantTitle
          } = variantsDevModel.entries().next().value.at(-1);
          trackVariantSelected({
            variantId,
            variantPrice: Number(variantPrice.amount),
            variantTitle
          });
        }
      }
      fetchAll();
    }
  }, [product, start, currentStepId, dataLoaded, setSelectionsWithOptions]);

  // Handle URL parsing after data is loaded
  useEffect(() => {
    if (!dataLoaded || loading) return;

    // Check for personalisation data in URL and auto-open modal if needed
    const urlParams = new URLSearchParams(window.location.search);
    const hasPersonalisationData = urlParams.has('personalisation');
    const openPersonalisationParam = urlParams.get('openPersonalisation');
    const shouldOpenModal = openPersonalisationParam === 'true';
    const explicitlyDisabled = openPersonalisationParam === 'false';

    // Only apply URL parameter logic if we have URL parameters
    // This prevents interference with normal button clicks
    if (hasPersonalisationData || openPersonalisationParam !== null) {
      // Only open modal if we have personalisation data OR explicitly told to open,
      // but NOT if explicitly disabled
      if ((hasPersonalisationData || shouldOpenModal) && !explicitlyDisabled) {
        if (hasPersonalisationData && !hasAppliedSharedUrlInitRef.current) {
          // Use setSelectionsWithOptions to populate from URL
          setSelectionsWithOptions((prev) => prev, { populateDefaultState: true });
          setIsSharedPersonalisation(true);
          // Always jump to the last step.
          if (personalisationSteps && personalisationSteps.size > 0) {
            const lastStepId = Array.from(personalisationSteps.keys()).at(-1);
            if (lastStepId) setCurrentStepId(lastStepId);
          }
          setPersonalisationModalOpen(true);
          hasAppliedSharedUrlInitRef.current = true;
        } else if (shouldOpenModal && !hasAppliedSharedUrlInitRef.current) {
          setIsSharedPersonalisation(false);
          // Just open the modal with current selections.
          setPersonalisationModalOpen(true);
          hasAppliedSharedUrlInitRef.current = true;
        }
      } else if (explicitlyDisabled) {
      }
    } else {
      // Normal button click flow - open modal after data is loaded
      setIsSharedPersonalisation(false);
      setPersonalisationModalOpen(true);
    }
  }, [dataLoaded, loading, setSelectionsWithOptions, personalisationSteps, setCurrentStepId]);

  // Keep the total price up to date.
  useEffect(() => {
    // Base price of product (value should be an integer in pence).
    const selectedBasePrice = selections.slots.find(({ id }) => id === baseProductConfigurationSlotId)?.selected;
    const baseProductVariant = variants.get(selectedBasePrice);

    let baseProductPrice = null;
    // If defined
    // Get base product variant
    // Get base product variant price
    // Add base product variant price to total price.
    if (baseProductVariant?.price?.amount) {
      baseProductPrice =
        (typeof baseProductVariant.price.amount === 'string'
          ? Number(baseProductVariant.price.amount)
          : baseProductVariant.price.amount) * 100;
    } else {
      baseProductPrice = typeof product?.price === 'number' ? product.price : 0;
    }

    /*
     * Personalisation Upsells
     * Value from storefront API is a string & in pound format, convert to a integer and then convert to pence for accurate calculation.
     */
    const slotUpsellTotalPrice = selections.slots
      .filter(({ selected, id }) => selected !== null && id !== baseProductConfigurationSlotId)
      .reduce((acc, selectionRow) => {
        const { selected, id, componentGroupId: slotSelectionGroupId } = selectionRow;
        const theSlot = personalisationSlots.get(id);

        // UV_INSERT: price lives in the resolved UV insert config themes, not the variants Map.
        if (theSlot?.slotType === 'UV_INSERT') {
          // `selected` is a UvInsertSlotSelection object for UV_INSERT slots, not a variant ID string.
          const uvSelected = /** @type {{ variantId: number, configHandle: string } | null} */ (selected);
          if (!uvSelected || !uvSelected.variantId) return acc;
          if (theSlot.isFreeSlot) return acc;

          const uvConfig = resolvedUvInserts?.configs.find((c) => c.handle === uvSelected.configHandle);

          const uvTheme = uvConfig?.themes.find((t) => Number(t.id) === uvSelected.variantId);
          if (!uvTheme?.price) return acc;

          const variantPricePounds = parseFloat(String(uvTheme.price)) / 100;

          const effectivePricePounds =
            typeof theSlot.overridePrice === 'number' ? theSlot.overridePrice : variantPricePounds;
          return acc + Math.round(effectivePricePounds * 100);
        }

        // IMAGE_UPLOAD photo-insert add-on: the +£X per photo lives on the slot's linked
        // photoVariant (a separate SKU), not in the variants Map. Mirror the cart-transform
        // pricing ("{slotId}:{photoVariantId}", see serialisePricingData / buildPersonalisationChoices)
        // so the journey total matches the mini cart — ALBAU-799.
        if (theSlot?.slotType === 'IMAGE_UPLOAD' && theSlot.photoVariant) {
          // GROUPED image uploads propagate the file to sibling slots (groupLineItemPropertyKey
          // === null); only the active group's slot is charged at cart, so skip the copies here
          // to avoid double-counting.
          if (theSlot.slotMode === 'GROUPED' && !selectionRow.groupLineItemPropertyKey) return acc;
          if (!selected) return acc; // no image uploaded yet
          if (theSlot.isFreeSlot) return acc;
          if (typeof theSlot.overridePrice === 'number') {
            return acc + Math.round(theSlot.overridePrice * 100);
          }
          if (typeof theSlot.photoVariantPrice !== 'number') return acc;
          return acc + Math.round(theSlot.photoVariantPrice * 100);
        }

        // Handle both single selections and arrays for upsells.
        if (Array.isArray(selected)) {
          // For upsells with multiple selections, sum all selected variants.
          return selected.reduce((slotAcc, variantId) => {
            const item = variants.get(variantId);
            if (!item) return slotAcc;

            if (theSlot.isFreeSlot) return slotAcc;
            if (typeof theSlot.overridePrice === 'number') {
              return slotAcc + Math.round(theSlot.overridePrice * 100);
            }

            const { priceModifierType, priceModifierValue } = getPriceModifiersForVariant(
              componentGroups,
              variantId,
              slotSelectionGroupId
            );

            // Calculate the final price with modifier applied.
            const originalPrice = typeof item.price.amount === 'string' ? Number(item.price.amount) : item.price.amount;
            const finalPrice = calculateFinalPriceModifier({
              priceModifierType: priceModifierType || '',
              priceModifierValue: Number(priceModifierValue) || 0,
              variantPrice: originalPrice
            });

            const addonAmountPence = Math.round(finalPrice * 100);
            return slotAcc + addonAmountPence;
          }, acc);
        } else {
          // For single selections, use existing logic.
          if (!selected) return acc;

          const item = variants.get(selected);

          if (theSlot.isFreeSlot) {
            return acc + 0;
          }

          // Apply override price if no item is found - used for non variant items.
          if (typeof theSlot.overridePrice === 'number') {
            return acc + Math.round(theSlot.overridePrice * 100);
          }

          if (!item) return acc;

          const { priceModifierType, priceModifierValue } = getPriceModifiersForVariant(
            componentGroups,
            selected,
            slotSelectionGroupId
          );

          // Calculate the final price with modifier applied.
          const originalPrice = typeof item.price.amount === 'string' ? Number(item.price.amount) : item.price.amount;

          const finalPrice = calculateFinalPriceModifier({
            priceModifierType: priceModifierType || '',
            priceModifierValue: Number(priceModifierValue) || 0,
            variantPrice: originalPrice
          });

          const addonAmountPence = Math.round(finalPrice * 100);
          return acc + addonAmountPence;
        }
      }, 0);

    const totalPrice = baseProductPrice + slotUpsellTotalPrice;

    setCurrentTotalPrice(totalPrice / 100);
  }, [selections, product, variants, componentGroups, personalisationSlots, resolvedUvInserts]);

  const updateSelections = useCallback(
    async ({
      slotId,
      itemSelected,
      groupLineItemPropertyKey = null,
      extraImages,
      supportingImage = null,
      componentGroupId: explicitComponentGroupId = null,
      replaceExtraImages
    }) => {
      let productVariantId = null;
      const theSlot = personalisationSlots.get(slotId);
      const {
        stepId,
        slotType,
        lineItemPropertyKey,
        visualisationGroup,
        targetImagePlaceholder,
        positionX,
        positionY,
        scale
      } = theSlot;

      // Track the last modified group for visualiser tab switching
      if (visualisationGroup?.groupId) {
        setLastModifiedGroupId(visualisationGroup.groupId);
      }
      let theVariant = null;

      if (!theSlot) {
        throw new Error(`Slot ${slotId} not found`);
      }

      // Handle both single selections and arrays for upsells
      const isArraySelection = Array.isArray(itemSelected);
      const singleItemSelected = isArraySelection ? (itemSelected.length > 0 ? itemSelected[0] : null) : itemSelected;

      // Variable to store cropped image for IMAGE_UPLOAD slots
      let croppedImage = null;

      switch (slotType) {
        case 'COMPONENT':
        case 'BASE_CONFIG':
          // For arrays, we need to handle each variant individually
          if (isArraySelection) {
            // For upsells with multiple selections, we don't need to validate each variant
            // as they will be handled differently in the cart
            theVariant = singleItemSelected ? variants.get(singleItemSelected) : null;
          } else {
            theVariant = itemSelected ? variants.get(itemSelected) : null;
            if (typeof theVariant === 'undefined') {
              throw new Error(`Variant ${itemSelected} not found`);
            }
          }
          break;
        case 'ALPHANUMERIC':
          break;
        case 'CHECKBOX':
          break;
        case 'UV_INSERT':
          // The selection object { variantId, templateId, configHandle, textByKey, imageByKey }
          // is built by PersonalisationUvInsertSlot.vm.ts and stored directly as selected.
          break;
        case 'IMAGE_UPLOAD':
          // Process image cropping if this is a masked image upload slot
          if (
            itemSelected instanceof File &&
            targetImagePlaceholder &&
            positionX != null &&
            positionY != null &&
            scale != null
          ) {
            try {
              // Calculate mask aspect ratio from targetImagePlaceholder dimensions
              const maskAspectRatio =
                targetImagePlaceholder?.width && targetImagePlaceholder?.height
                  ? targetImagePlaceholder.height / targetImagePlaceholder.width
                  : undefined; // Will default to 9/16 in crop function

              // Use supportingImage (which may include the mask outline) for
              // cropping when available; fall back to itemSelected otherwise.
              const cropSourceImage = supportingImage instanceof File ? supportingImage : itemSelected;

              croppedImage = await cropMaskedImageWithPadding({
                imageFile: cropSourceImage,
                positioning: {
                  x: positionX,
                  y: positionY,
                  scale: scale
                },
                maskAspectRatio
                // padding defaults to 10
              });
            } catch (error) {
              // Error already logged in cropMaskedImageWithPadding
              // Continue without cropped image if cropping fails
              logWarning({
                label: 'updateSelections',
                warning: 'Image cropping failed, croppedImage will be null',
                message: 'Image cropping failed, croppedImage will be null',
                extraData: { slotId, error }
              });
            }
          }
          break;
        default:
          // @TODO: tighten up the slot type handling.
          break;
      }

      // If we're on the base product configuration slot - we want to update the product variant id.
      if (slotId === baseProductConfigurationSlotId) {
        productVariantId = singleItemSelected;
        const variant = variants.get(productVariantId);

        // Only track variant selection if we have a valid variant.
        if (variant && variant.price && variant.price.amount) {
          trackVariantSelected({
            variantId: productVariantId,
            variantPrice: Number(variant.price.amount),
            variantTitle: variant.title
          });
        }
      }

      const usesComponentGroupSelection = slotType === 'COMPONENT' || slotType === 'BASE_CONFIG';
      const resolvedComponentGroupId = usesComponentGroupSelection
        ? resolveSelectionComponentGroupId({
            componentGroups,
            explicitGroupId: explicitComponentGroupId,
            itemSelected,
            variantFallbackGroupId: theVariant?.componentGroupId ?? null
          })
        : undefined;

      setSelections((prev) => {
        const newState = {
          ...prev,
          ...(productVariantId && { productVariantId }),
          slots: prev.slots.map((slot) => {
            if (slot.id === slotId) {
              const updatedSlot = {
                ...slot,
                groupLineItemPropertyKey,
                lineItemPropertyKey,
                selected: itemSelected,
                extraImages:
                  replaceExtraImages !== undefined
                    ? replaceExtraImages
                    : extraImages !== null && extraImages !== undefined
                      ? slot.extraImages
                        ? [...slot.extraImages, extraImages]
                        : [extraImages]
                      : slot.extraImages,
                ...(supportingImage !== undefined && { supportingImage }),
                ...(usesComponentGroupSelection && {
                  componentGroupId: resolvedComponentGroupId
                }),
                // Store cropped image if available (for IMAGE_UPLOAD slots with mask)
                // The original itemSelected (masked image) is kept for the visualizer API
                // The croppedImage will be used for line item properties
                ...(croppedImage !== null && { croppedImage })
              };
              return updatedSlot;
            }
            return slot;
          })
        };
        return newState;
      });

      const stepNumber = Array.from(personalisationSteps.keys()).indexOf(stepId);
      const step = personalisationSteps.get(stepId);
      const slotPosition = step?.slots?.findIndex((id) => id === slotId);

      // Skip the base product configuration slot as it's not a real slot.
      if (itemSelected && stepId !== baseProductConfigurationStepId) {
        // For arrays, track each selection individually
        if (isArraySelection) {
          itemSelected.forEach((selectedItem) => {
            trackSlotPopulated({
              slotName: theSlot.handle,
              slotType: slotType,
              slotPosition: slotPosition,
              slotValue: selectedItem,
              stepNumber: Number(stepNumber),
              stepName: stepId
            });
          });
        } else {
          trackSlotPopulated({
            slotName: theSlot.handle,
            slotType: slotType,
            slotPosition: slotPosition,
            slotValue: itemSelected,
            stepNumber: Number(stepNumber),
            stepName: stepId
          });
        }
      }
      if (itemSelected === null && stepId !== baseProductConfigurationStepId) {
        trackSlotRemoved({
          slotName: theSlot.handle,
          slotType: slotType,
          slotPosition: slotPosition,
          stepNumber: Number(stepNumber),
          stepName: stepId
        });
      }
      if (stepId === baseProductConfigurationStepId) {
        // Reset the hidden state of the base product configuration step.
        setAllHiddenToFalse({ setPersonalisationSteps, setPersonalisationSlots, setSelections });
      }
      hideSlotSelections({ hideSlotsOnVariantsSettings, productVariantId, setSelections, setPersonalisationSlots });
    },
    [personalisationSlots, personalisationSteps, variants, componentGroups, hideSlotsOnVariantsSettings]
  );

  // Hide steps that don't have any slots left.
  useHideSteps({ personalisationSlots, setPersonalisationSteps, selectionsSlots: selections.slots });

  const resetJourney = useCallback(async () => {
    setIsResetting(true);

    const ids = Array.from(personalisationSteps.keys());
    if (ids.length > 0) {
      setCurrentStepId(ids[0]);
    } else {
      setCurrentStepId('');
    }

    if (
      personalisationSlots &&
      typeof personalisationSlots.values === 'function' &&
      typeof personalisationSlots.size === 'number' &&
      !Array.isArray(personalisationSlots)
    ) {
      const slotsMap = personalisationSlots;
      setSelections({
        productVariantId: product?.variants.length === 1 ? product.variants[0].id : null,
        slots: Array.from(slotsMap.values()).map(
          ({
            id,
            stepId,
            lineItemPropertyKey,
            slotType,
            slotMode,
            isRequired,
            hidden,
            visualised,
            visualisationGroup,
            translatedLineItemPropertyKey,
            photoVariant,
            photoVariantTemplateId
          }) => ({
            id,
            selected: null,
            componentGroupId: null,
            stepId,
            lineItemPropertyKey,
            slotType,
            slotMode,
            isRequired,
            groupLineItemPropertyKey: null,
            hidden,
            visualised,
            visualisationGroup: visualisationGroup || null,
            translatedLineItemPropertyKey: translatedLineItemPropertyKey || null,
            extraImages: [],
            photoVariant: photoVariant || null,
            photoVariantTemplateId: photoVariantTemplateId || null
          })
        )
      });
    } else {
      setSelections({
        productVariantId: product?.variants.length === 1 ? product.variants[0].id : null,
        slots: []
      });
    }
    setSelectedVariantId(null);
    setCurrentSlotId(0);
    setCurrentTotalPrice(0);
    setIsSharedPersonalisation(false);

    // Clear personalisation parameters from URL.
    clearPersonalisationFromUrl();

    await wait({ ms: 300 });
    setIsResetting(false);
  }, [
    personalisationSteps,
    setCurrentStepId,
    setSelections,
    personalisationSlots,
    setSelectedVariantId,
    setCurrentSlotId,
    setCurrentTotalPrice,
    product
  ]);

  /**
   * Reorders the slots in selections based on a new order of slot IDs.
   * @param {object} props - The component props.
   * @param {string[]} props.newOrder - Array of slot IDs in the new order.
   */
  const reorderSlotValues = useCallback(({ newOrder }) => {
    setSelections((prev) => {
      // No re-ordering required if the new order is empty.
      if (newOrder.length === 0) {
        return prev;
      }
      // Find all component slots and their current selected values
      const firstItemFromNewOrder = newOrder.at(0);
      const lastItemFromNewOrder = newOrder.at(-1);

      // Find where the items are that are being reordered in the new order.
      const positionOfItemsBeforeFirstItem = prev.slots.findIndex(({ id }) => id === firstItemFromNewOrder);
      // +1 Because of the item just removed.
      const positionOfItemsAfterLastItem = prev.slots.findIndex(({ id }) => id === lastItemFromNewOrder) + 1;

      // Split prev.slots into three arrays - items before the first item, items between the first and last item, and items after the last item.
      const itemsBeforeFirstItem = prev.slots.slice(0, positionOfItemsBeforeFirstItem);
      const itemsBetweenFirstAndLastItem = prev.slots.slice(
        positionOfItemsBeforeFirstItem,
        positionOfItemsAfterLastItem
      );
      const itemsAfterLastItem = prev.slots.slice(positionOfItemsAfterLastItem);

      // Reorder the items between the first and last item.
      const componentSlotIds = itemsBetweenFirstAndLastItem.map(({ id }) => id);
      const selectedValues = itemsBetweenFirstAndLastItem.map(({ selected }) => selected);
      const baseProductSlot = itemsBetweenFirstAndLastItem.find(({ id }) => id === baseProductConfigurationSlotId);

      // Reorder the selected values according to newOrder
      const reorderedSelectedValues = newOrder.map((id) => {
        const idx = componentSlotIds.indexOf(id);
        return idx !== -1 ? selectedValues[`${idx}`] : null;
      });

      let reorderedIndex = 0;
      // Assign the reordered selected values back to the component slots in their original positions
      const newSlots = itemsBetweenFirstAndLastItem.map((slot) => {
        // Leave the base product slot as is - its always last.
        if (slot.id === baseProductConfigurationSlotId) {
          return {
            ...slot,
            selected: baseProductSlot?.selected ?? null
          };
        }
        return {
          ...slot,
          selected: reorderedSelectedValues[reorderedIndex++] ?? null
        };
      });

      return {
        ...prev,
        slots: [...itemsBeforeFirstItem, ...newSlots, ...itemsAfterLastItem]
      };
    });
  }, []);

  // Handle hiding slots and steps based on the variant selection.
  const { filteredPersonalisationSteps, filteredPersonalisationSlots, filteredSelections } =
    useCacheFilteredPersonalisationStepsAndSlots({ personalisationSteps, personalisationSlots, selections });

  // Handle visualisation updates and management.
  const { visualisationUrls } = useVisualisationUrlManagement({
    deps: {
      filteredSelections,
      personalisationId: personalisation?.id,
      visualiserSlotMode,
      variants
    },
    baseProductConfigurationSlotId,
    slots: Array.from(filteredPersonalisationSlots.values()),
    steps: personalisationSteps
  });

  // Extract visualisation groups from slots
  useEffect(() => {
    const groups = new Map();
    Array.from(personalisationSlots.values()).forEach((slot) => {
      if (slot.visualisationGroup) {
        groups.set(slot.visualisationGroup.groupId, {
          label: slot.visualisationGroup.label,
          visualisationType: slot.visualisationGroup.visualisationType,
          supportingImageLineItemPropertyKey: slot.visualisationGroup.supportingImageLineItemPropertyKey
        });
      }
    });
    setVisualisationGroups(groups);
  }, [personalisationSlots]);

  const updateTheCart = useCallback(
    async ({ country, onSuccess }) => {
      if (!product) {
        throw new Error('No product selected yet');
      }
      // Check if there's a base product configuration slot, otherwise use the productVariantId
      const baseProductId =
        selections.slots.find((slot) => slot.id === baseProductConfigurationSlotId)?.selected ||
        selections.productVariantId;
      if (!baseProductId) {
        throw new Error('No base product selected yet');
      }

      setIsAddingToCart(true);
      setIsRetrying(false); // Reset retry state at start

      try {
        trackPersonalisationComplete({
          totalPrice: currentTotalPrice,
          totalSteps: personalisationSteps.size
        });

        // Build choices array using extracted utility.
        // Use filteredSelections (hidden slots excluded) so that slots belonging to a
        // previously-selected journey are not included in the cart payload.
        const choices = buildChoicesFromSelections({ selections: filteredSelections, variants, resolvedUvInserts });

        await addToCart({
          baseProduct: {
            id: baseProductId,
            quantity: 1
          },
          slots: filteredSelections.slots,
          country,
          choices,
          visualiserImageUrls: await visualisationUrls,
          baseAttributes: filteredSelections.slots.reduce(
            processBaseAttributes({ variants, personalisationSlots }),
            {}
          ),
          selections: filteredSelections,
          templateId: variants.get(baseProductId)?.templateId || null,
          onRetryStart: () => setIsRetrying(true),
          onRetryEnd: () => setIsRetrying(false)
        });

        // Call success callback if provided (to close modal)
        if (onSuccess) {
          // Wait for modal closing animation to complete, then open cart drawer
          setTimeout(async () => {
            onSuccess();
            // Open cart drawer after modal closes
            // @ts-ignore - CartDrawer is added to window at runtime
            await window.CartDrawer?.open?.();
            // Reset journey after cart drawer opens
            await resetJourney();
          }, 400);
        }
      } catch (error) {
        console.error('Failed to add to cart:', error);
        throw error;
      } finally {
        setIsAddingToCart(false);
        setIsRetrying(false); // Ensure retry state is reset
      }
    },
    [
      product,
      filteredSelections,
      currentTotalPrice,
      personalisationSteps,
      personalisationSlots,
      variants,
      resolvedUvInserts,
      resetJourney,
      setIsAddingToCart,
      visualisationUrls
    ]
  );

  return (
    <PersonalisationContext.Provider
      value={{
        currentTotalPrice,
        setCurrentTotalPrice,
        currentStepId,
        setCurrentStepId,
        currentSlotId,
        setCurrentSlotId,
        personalisation,
        personalisationSteps: filteredPersonalisationSteps,
        personalisationSlots: filteredPersonalisationSlots,
        selectedVariantId,
        setSelectedVariantId,
        variants,
        addonGroups,
        addonItems,
        componentGroups,
        loading,
        isAddingToCart,
        isRetrying,
        setIsRetrying,
        selections: filteredSelections,
        setSelections,
        setSelectionsWithOptions,
        product,
        updateSelections,
        updateTheCart,
        reorderSlotValues,
        setStart,
        personalisationModalOpen,
        setPersonalisationModalOpen,
        start,
        resetJourney,
        isResetting,
        personalisationId: personalisation?.id,
        visualiserImageUrl: visualisationUrls,
        visualisationGroups,
        lastModifiedGroupId,
        hasPersonalisationOptions,
        personalisationValidationErrors,
        setPersonalisationValidationErrors,
        isSharedPersonalisation,
        resolvedUvInserts,
        activeUvInsertChooser,
        setActiveUvInsertChooser
      }}
    >
      {loading ? (
        <button className="button button--primary button--orange button--full-width">Loading...</button>
      ) : (
        children
      )}
    </PersonalisationContext.Provider>
  );
};

// Custom hook to use the context
export const usePersonalisationContext = () => {
  const context = useContext(PersonalisationContext);
  if (!context) {
    throw new Error('useAppContext must be used within PersonalisationProvider');
  }
  return context;
};
